import { Sparkles } from 'lucide-react'
import { useAuth } from '@/features/auth'

/**
 * Today's remaining AI summaries.
 *
 * The backend has priced this per tier since tiers landed (`TIER_DAILY_CREDITS`), and
 * the only component that ever showed it was deleted as an orphan in P2-7. So a user
 * spent summaries with no idea how many were left and met the cap as a bare 429 — an
 * app that reads as broken, not as full.
 *
 * The numbers come from `user.preferences`, which is the `/auth/me` payload the store
 * already holds; `GET /preferences/{id}/credits` returns the same three values and is
 * a round trip to learn what is on hand.
 *
 * No price, no upgrade link. Whether a paid tier can exist at all is still open
 * (P3-14: Naver's terms 7.2.③ may forbid charging for anything built on their API),
 * and a quota display has to keep working if the answer is no.
 */
export function AISummaryQuota() {
  const { user } = useAuth()

  // Signed out there is no account to bill and the route 401s before any counting.
  if (!user) {
    return null
  }

  const { remaining_credits: remaining, daily_credit_limit: limit } = user.preferences
  const exhausted = remaining === 0

  return (
    <p
      className={`mt-2 flex items-center gap-1.5 text-xs ${
        exhausted ? 'text-amber-700 dark:text-amber-500' : 'text-muted-foreground'
      }`}
      // The count changes after each summary; announce it without stealing focus.
      role="status"
      aria-live="polite"
    >
      <Sparkles className="h-3 w-3 shrink-0" aria-hidden="true" />
      {exhausted ? (
        <span>
          오늘 AI 요약 <span className="tabular-nums">{limit}</span>회를 다 썼어요. 자정(UTC)에 다시
          채워져요.
        </span>
      ) : (
        <span>
          오늘 AI 요약 <span className="font-medium tabular-nums">{remaining}</span>
          <span className="tabular-nums">/{limit}</span>회 남았어요.
        </span>
      )}
    </p>
  )
}
